import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings, Bell, Lock, User, CheckCircle, AlertCircle, Trash2, Edit, ImagePlus, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { cn, fileToDataUrl, createThumbnail } from '../utils/formatters';
import { btnGhost, modalOverlay, modalContent } from '../styles/components';

export function SettingsPage() {
  const { user } = useAuth();
  const { company, isLoading, updateCompany } = useCompany();

  const [message] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoMessage, setLogoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const onLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setLogoMessage({ type: 'error', text: 'El archivo debe ser una imagen.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoMessage({ type: 'error', text: 'La imagen no debe superar 5MB.' });
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    const thumb = await createThumbnail(dataUrl, 400);
    setLogoPreview(thumb);
    setLogoMessage(null);
  };

  const saveLogo = async () => {
    if (!updateCompany) return;
    setLogoSaving(true);
    try {
      await updateCompany({ logo: logoPreview });
      setLogoPreview('');
      setLogoMessage({ type: 'success', text: 'Logotipo actualizado.' });
    } catch {
      setLogoMessage({ type: 'error', text: 'No se pudo guardar el logotipo.' });
    } finally {
      setLogoSaving(false);
    }
  };

  const removeLogo = async () => {
    if (!updateCompany) return;
    setLogoSaving(true);
    try {
      await updateCompany({ logo: undefined });
      setLogoPreview('');
      setLogoMessage({ type: 'success', text: 'Logotipo eliminado.' });
    } catch {
      setLogoMessage({ type: 'error', text: 'No se pudo eliminar el logotipo.' });
    } finally {
      setLogoSaving(false);
    }
  };

  const currentLogo = logoPreview || company?.logo || '';

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-[color:var(--b2b-danger)]" />
          <h1 className="text-2xl font-bold mb-4">Sesión no disponible</h1>
          <Link to="/login" className="b2b-btn b2b-btn--primary">Iniciar sesión</Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center">
          <Settings className="w-12 h-12 mx-auto mb-4 opacity-40 animate-spin" />
          <p className="b2b-muted">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="space-y-1">
          <span className="b2b-eyebrow">Ajustes</span>
          <h1 className="text-3xl font-extrabold tracking-tight">Configuración</h1>
          <p className="b2b-muted">Preferencias de la cuenta y empresa</p>
        </div>
        <Link to="/profile" className="b2b-btn b2b-btn--ghost">
          <User className="w-4 h-4" />
          Volver al perfil
        </Link>
      </div>

      <div className="b2b-panel p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">Cuenta</h2>
          <Link to="/profile" className="b2b-btn b2b-btn--ghost b2b-btn--sm">
            <Edit className="w-4 h-4" />
            Editar perfil
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <p className="b2b-card__label">Nombre</p>
            <p className="text-lg font-semibold">{user.name}</p>
          </div>
          <div>
            <p className="b2b-card__label">Email</p>
            <p className="text-lg font-semibold break-all">{user.email}</p>
          </div>
          <div>
            <p className="b2b-card__label">Rol</p>
            <p className="text-lg font-semibold capitalize">{user.role}</p>
          </div>
          <div>
            <p className="b2b-card__label">Empresa</p>
            <p className="text-lg font-semibold">{company?.name || 'Sin empresa asignada'}</p>
          </div>
        </div>
      </div>

      {company && (
        <div className="b2b-panel p-6">
          <h2 className="text-lg font-semibold mb-1">Logotipo de la empresa</h2>
          <p className="b2b-muted text-sm mb-5">Aparece en los presupuestos y en el portfolio público.</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="w-28 h-28 rounded-xl border border-[var(--b2b-border)] bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center overflow-hidden shrink-0">
              {currentLogo ? (
                <img src={currentLogo} alt={company.name} className="w-full h-full object-contain" />
              ) : (
                <ImagePlus className="w-7 h-7 text-secondary-400" />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="b2b-btn b2b-btn--ghost cursor-pointer">
                <ImagePlus className="w-4 h-4" />
                <span>Elegir imagen…</span>
                <input type="file" accept="image/*" onChange={onLogoFile} className="sr-only" />
              </label>
              <button
                type="button"
                onClick={saveLogo}
                disabled={!logoPreview || logoSaving}
                className="b2b-btn b2b-btn--primary disabled:opacity-50"
              >
                {logoSaving ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </span>
                ) : (
                  'Guardar logotipo'
                )}
              </button>
              {company.logo && (
                <button type="button" onClick={removeLogo} disabled={logoSaving} className="b2b-btn b2b-btn--ghost">
                  <Trash2 className="w-4 h-4" /> Quitar
                </button>
              )}
            </div>
          </div>
          <p className="b2b-muted text-xs mt-4">JPG, PNG o WebP. Máximo 5MB. Recomendado fondo transparente.</p>
          {logoMessage && (
            <div
              className={cn(
                'mt-4 flex items-center gap-2 text-sm',
                logoMessage.type === 'success' ? 'text-[color:var(--b2b-success)]' : 'text-[color:var(--b2b-danger)]'
              )}
              role="status"
            >
              {logoMessage.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {logoMessage.text}
            </div>
          )}
        </div>
      )}

      {company && (
        <div className="b2b-panel p-6">
          <h2 className="text-lg font-semibold mb-6">Empresa</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="b2b-card__label">Nombre comercial</p>
              <p className="text-lg font-semibold">{company.name}</p>
            </div>
            <div>
              <p className="b2b-card__label">Razón social</p>
              <p className="text-lg font-semibold">{company.fiscalName || '—'}</p>
            </div>
            <div>
              <p className="b2b-card__label">CIF/NIF</p>
              <p className="text-lg font-semibold">{company.cif || '—'}</p>
            </div>
            <div>
              <p className="b2b-card__label">Dirección</p>
              <p className="text-lg font-semibold">{company.address || '—'}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="b2b-card__label">Datos fiscales y textos legales</p>
              <p className="b2b-muted">
                IVA: {company.vatRate || 21}% · Condiciones de pago: {company.paymentTerms || 'Pago 50% a la firma del presupuesto y 50% a la finalización de la obra.'}
              </p>
              <p className="b2b-muted mt-1">
                Cláusulas de garantía: {company.warrantyClauses || 'Garantía de 12 meses en mano de obra y materiales según Ley de Ordenación de la Edificación.'}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="b2b-panel p-6">
        <h2 className="text-lg font-semibold mb-4">Preferencias</h2>
        <div className="space-y-1">
          <div className="b2b-panel__row">
            <span className="b2b-icon"><Settings className="w-5 h-5" /></span>
            <span className="flex-1">
              <span className="block font-medium">Notificaciones por email</span>
              <span className="block text-sm b2b-muted">Recibir alertas de presupuestos vencidos y gastos importantes</span>
            </span>
            <CheckCircle className="w-5 h-5 text-[color:var(--b2b-success)]" />
          </div>
          <div className="b2b-panel__row">
            <span className="b2b-icon"><Bell className="w-5 h-5" /></span>
            <span className="flex-1">
              <span className="block font-medium">Actualizaciones automáticas</span>
              <span className="block text-sm b2b-muted">Mantener la aplicación actualizada con las últimas funcionalidades</span>
            </span>
            <CheckCircle className="w-5 h-5 text-[color:var(--b2b-success)]" />
          </div>
          <div className="b2b-panel__row">
            <span className="b2b-icon"><Lock className="w-5 h-5" /></span>
            <span className="flex-1">
              <span className="block font-medium">Seguridad de la cuenta</span>
              <span className="block text-sm b2b-muted">Autenticación de dos factores (próximamente)</span>
            </span>
            <CheckCircle className="w-5 h-5 opacity-40" />
          </div>
        </div>
      </div>

      {message && (
        <div className={cn(
          'flex items-center gap-3 p-4 rounded-lg',
          message.type === 'success' ? 'b2b-pill b2b-pill--ok' : 'b2b-pill b2b-pill--danger'
        )} role="alert">
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="pt-6 border-t border-[var(--b2b-border)]">
        <button onClick={() => setShowDeleteAccount(true)} className="b2b-btn b2b-btn--danger">
          <Trash2 className="w-4 h-4" />
          Eliminar cuenta
        </button>

        {showDeleteAccount && (
          <div className={modalOverlay}>
            <div className={cn(modalContent, 'max-w-md p-8')} role="dialog" aria-modal="true">
              <h2 className="text-xl font-bold text-center mb-4">Eliminar cuenta</h2>
              <p className="b2b-muted mb-8 text-center">
                ¿Estás seguro de querer eliminar tu cuenta?<br />
                <strong>Esta acción es irreversible y eliminará todos tus datos.</strong>
              </p>
              <div className="flex gap-3 justify-end pt-6 border-t border-[var(--b2b-border)]">
                <button onClick={() => setShowDeleteAccount(false)} className={btnGhost}>Cancelar</button>
                <button
                  onClick={() => {
                    alert('Eliminar cuenta (simulado)');
                    setShowDeleteAccount(false);
                  }}
                  className="b2b-btn b2b-btn--danger"
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar cuenta
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
